// Foot Template Configuration
// Questions simplified to 12-year-old reading level

export const footTemplate = {
  id: 'FOOT',
  name: 'Foot',
  icon: '🦶',
  dominantSide: 'leg',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Which foot has the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    problemDate: {
      type: 'date',
      label: 'When did your foot problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Slowly over time'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Pain',
        'Swelling',
        'Stiffness',
        'Numbness or tingling',
        'Burning feeling',
        'Trouble walking',
        'Change in foot shape',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Please describe the other problem:',
      placeholder: 'Tell us about your other problem',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    problemDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your foot problem started',
      rows: 3,
      required: true
    },

    // Pain Section
    painLocation: {
      type: 'checkbox-multiple',
      label: 'Where exactly is your pain?',
      options: [
        'Ball of foot (front)',
        'Middle of foot (arch area)',
        'Heel',
        'Bottom of foot (sole)',
        'Top of foot',
        'Toes',
        'All over the foot'
      ]
    },

    painType: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Sharp', 'Dull', 'Throbbing', 'Burning', 'Stabbing', 'Other']
    },

    painTypeOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painType', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painRadiation: {
      type: 'radio',
      label: 'Does the pain spread anywhere else?',
      options: ['Yes, to my ankle', 'Yes, up my leg', 'Yes, to my toes', 'No, just where it hurts']
    },

    painTiming: {
      type: 'checkbox-multiple',
      label: 'When is your pain worst?',
      options: [
        'All the time',
        'First thing in the morning',
        'After sitting/resting',
        'When walking or standing',
        'At night',
        'After exercise'
      ]
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Walking',
        'Standing for a long time',
        'Running',
        'Certain shoes',
        'Cold weather',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Putting feet up',
        'Pain medication',
        'Warm water soak',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Swelling Section
    hasSwelling: {
      type: 'radio',
      label: 'Is your foot swollen?',
      options: ['Yes', 'No']
    },

    swellingLocation: {
      type: 'text',
      label: 'Where is the swelling?',
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    swellingType: {
      type: 'radio',
      label: 'Is the swelling:',
      options: ['In one spot', 'All over the foot'],
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    swellingChange: {
      type: 'radio',
      label: 'Is the swelling:',
      options: ['Getting worse', 'Getting better', 'Staying the same'],
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    hasRedness: {
      type: 'radio',
      label: 'Is the area red or warm to touch?',
      options: ['Yes', 'No']
    },

    hadTrauma: {
      type: 'radio',
      label: 'Did you injure your foot (fall, drop something on it, twist it)?',
      options: ['Yes', 'No']
    },

    traumaDescription: {
      type: 'text',
      label: 'What happened?',
      showWhen: { field: 'hadTrauma', equals: 'Yes' }
    },

    // Stiffness Section
    hasStiffness: {
      type: 'radio',
      label: 'Is your foot stiff?',
      options: ['Yes', 'No']
    },

    stiffnessLocation: {
      type: 'checkbox-multiple',
      label: 'Which part is stiff?',
      options: ['Toes', 'Middle of foot', 'Back of foot/ankle'],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    morningStiffness: {
      type: 'radio',
      label: 'Is your foot worse first thing in the morning?',
      options: ['Yes', 'No']
    },

    // Nerve Symptoms
    hasNumbness: {
      type: 'radio',
      label: 'Do you have any numbness, tingling, or burning in your foot?',
      options: ['Yes', 'No']
    },

    numbnessLocation: {
      type: 'text',
      label: 'Where do you feel the numbness/tingling/burning?',
      showWhen: { field: 'hasNumbness', equals: 'Yes' }
    },

    // Instability/Gait
    balanceProblems: {
      type: 'radio',
      label: 'Do you have trouble with balance?',
      options: ['Yes', 'No']
    },

    unevenSurfaces: {
      type: 'radio',
      label: 'Is it hard to walk on uneven ground (like grass or gravel)?',
      options: ['Yes', 'No']
    },

    walkingChanged: {
      type: 'radio',
      label: 'Has the way you walk changed because of the pain?',
      options: ['Yes', 'No', 'Not sure']
    },

    // Functional Limitations
    standingDifficulty: {
      type: 'radio',
      label: 'Can you stand comfortably?',
      options: ['Yes', 'Yes but only for a short time', 'No, it hurts too much']
    },

    walkingDifficulty: {
      type: 'radio',
      label: 'Can you walk normally?',
      options: ['Yes', 'Yes but it hurts', 'No, I limp', 'No, I need help walking']
    },

    runningDifficulty: {
      type: 'radio',
      label: 'Can you run?',
      options: ['Yes', 'Yes but it hurts', 'No']
    },

    stairsDifficulty: {
      type: 'radio',
      label: 'Can you climb stairs?',
      options: ['Yes', 'Yes but it\'s hard', 'No']
    },

    shoesDifficulty: {
      type: 'radio',
      label: 'Can you wear normal shoes?',
      options: ['Yes', 'Only some shoes', 'No, I can only wear certain shoes']
    },

    // Previous History
    previousFootProblems: {
      type: 'radio',
      label: 'Have you had foot problems before?',
      options: ['Yes', 'No']
    },

    previousFootDetails: {
      type: 'textarea',
      label: 'Tell us about your previous foot problems:',
      rows: 2,
      showWhen: { field: 'previousFootProblems', equals: 'Yes' }
    },

    previousFootSurgery: {
      type: 'radio',
      label: 'Have you had foot surgery before?',
      options: ['Yes', 'No']
    },

    footType: {
      type: 'radio',
      label: 'Do you have flat feet or high arches?',
      options: ['Flat feet', 'High arches', 'Normal', 'Not sure']
    },

    familyFootProblems: {
      type: 'checkbox-multiple',
      label: 'Does anyone in your family have foot problems?',
      options: [
        'Bunions',
        'Flat feet',
        'High arches',
        'Arthritis',
        'Diabetes',
        'No/Not sure'
      ]
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Diabetes',
        'Gout',
        'Rheumatoid arthritis',
        'Poor circulation',
        'Nerve problems (neuropathy)',
        'None of these'
      ]
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies'
    },

    // Lifestyle
    occupation: {
      type: 'text',
      label: 'What is your job?',
      placeholder: 'Tell us what you do for work'
    },

    occupationType: {
      type: 'radio',
      label: 'At work, do you mostly:',
      options: ['Sit', 'Stand', 'Walk around', 'Mix of sitting and standing']
    },

    sport: {
      type: 'text',
      label: 'What sports or exercise do you do?',
      placeholder: 'e.g., running, football, hiking, dancing'
    },

    footwear: {
      type: 'text',
      label: 'What kind of shoes do you usually wear?',
      placeholder: 'e.g., runners, work boots, heels, flat shoes'
    },

    barefoot: {
      type: 'radio',
      label: 'Do you often go barefoot?',
      options: ['Yes', 'No']
    },

    smoking: {
      type: 'radio',
      label: 'Do you smoke?',
      options: ['No, never smoked', 'I used to smoke', 'Yes, I smoke']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'problem',
      title: '2. About Your Foot Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuredSide', 'problemDate', 'onsetType', 'mainProblem', 'mainProblemOther', 'problemDescription', 'hadTrauma', 'traumaDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painType', 'painTypeOther', 'painSeverity', 'painRadiation', 'painTiming', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'swelling',
      title: '4. Swelling & Changes',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasSwelling', 'swellingLocation', 'swellingType', 'swellingChange', 'hasRedness']
    },
    {
      id: 'stiffness',
      title: '5. Stiffness',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasStiffness', 'stiffnessLocation', 'morningStiffness']
    },
    {
      id: 'nerves',
      title: '6. Numbness & Tingling',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasNumbness', 'numbnessLocation']
    },
    {
      id: 'walking',
      title: '7. Walking & Balance',
      component: 'GenericSection',
      enabled: true,
      fields: ['balanceProblems', 'unevenSurfaces', 'walkingChanged']
    },
    {
      id: 'functional',
      title: '8. What You Can & Can\'t Do',
      component: 'GenericSection',
      enabled: true,
      fields: ['standingDifficulty', 'walkingDifficulty', 'runningDifficulty', 'stairsDifficulty', 'shoesDifficulty']
    },
    {
      id: 'history',
      title: '9. Previous Problems',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousFootProblems', 'previousFootDetails', 'previousFootSurgery', 'footType', 'familyFootProblems']
    },
    {
      id: 'medical',
      title: '10. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true
    },    {
      id: 'exerciseVitalSign',
      title: '11. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '12. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Plantar fasciitis',
      'Metatarsalgia',
      'Morton\'s neuroma',
      'Stress fracture',
      'Bunion (hallux valgus)',
      'Hammer toe',
      'Plantar plate tear',
      'Midfoot arthritis',
      'Tarsal tunnel syndrome'
    ]
  }
};

export default footTemplate;
